import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * Component to configure and manage usage limits for a campaign, including maximum discount, quantity limits, and multi-use settings.
 * @component
 * @selector app-usage-limits
 * @example
 *   <app-usage-limits [campaignData]="campaign" [isExpanded]="true" [isCompleted]="false" (dataChange)="onDataChange($event)" (toggle)="onToggle($event)" (done)="onDone()"></app-usage-limits>
 * @input {boolean} isExpanded - Controls whether the collapsible usage limits section is expanded (default: true).
 * @input {any} campaignData - Optional initial campaign data used to populate form fields (shape: { max_discount?, quantity_limit?, usage_type?, multi_use_limit? }).
 * @input {boolean} isCompleted - Indicates whether the usage limits step is marked as completed (used for UI state).
 * @output {EventEmitter<boolean>} toggle - Emitted when the usage limits section is toggled; payload is the new expanded state.
 * @output {EventEmitter<void>} done - Emitted when the user finishes editing and the form passes validation.
 * @output {EventEmitter<any>} dataChange - Emitted whenever form data changes; payload is the current data object { max_discount, quantity_limit, usage_type, multi_use_limit }.
 *
 * Purpose:
 *   Provides a UI and logic to edit usage-related constraints for a campaign (maximum discount amount, quantity limits, single vs multiple usage and multi-use limits).
 *
 * Key methods:
 *   - onUsageLimitsToggle(expanded: boolean): Emits the toggle event with the expanded state.
 *   - onUsageLimitsDone(): Validates the form and emits done if validation passes.
 *   - incrementDiscountAmount()/decrementDiscountAmount(): Safely increment/decrement maximumDiscountAmount with a 9-digit cap.
 *   - incrementQuantityLimit()/decrementQuantityLimit(): Safely increment/decrement quantityLimit with a 9-digit cap.
 *   - incrementMultiUseLimit()/decrementMultiUseLimit(): Increment/decrement multiUseLimit (used when usage type is 'multiple').
 *   - isDiscountDecrementDisabled()/isQuantityDecrementDisabled()/isMultiUseDecrementDisabled(): Helpers to determine if decrement controls should be disabled.
 *   - selectUsageType(type): Switches between 'single' and 'multiple' usage and triggers form change.
 *   - preventMinusKey(event): Prevents minus/hyphen keys from being entered into numeric inputs.
 *   - preventNegativeValue(field, event): Clears or resets negative numeric input values and enforces field-specific rules.
 *   - enforceMaxDigits(field, event): Ensures numeric inputs do not exceed 9 digits, truncating and updating the model if needed.
 *   - onFormChange(): Normalizes values (non-negative, 9-digit limits), clears errors, builds the data object and emits dataChange.
 *   - validateForm(): Validates current form state (9-digit maximums, required usage type, multi-use minimum) and populates errors/isFormValid.
 *   - clearErrors(): Resets validation errors and marks form as valid.
 *   - hasError(field)/getError(field): Helpers to check for and retrieve error messages for a given field.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Initializes form fields from campaignData if provided.
 *   - ngOnChanges(): Re-syncs form fields when incoming campaignData input changes.
 *
 * Validation & constraints:
 *   - Numeric fields (maximumDiscountAmount, quantityLimit) enforce a maximum of 9 digits (<= 999,999,999) and disallow negative values.
 *   - multiUseLimit must be at least 1 when usageType is 'multiple'.
 *   - All changes emit a normalized data object via dataChange so parent components can stay in sync.
 */
@Component({
  selector: 'app-usage-limits',
  imports: [CollapsibleSectionComponent, FormsModule, CommonModule],
  templateUrl: './usage-limits.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './usage-limits.component.scss',
})
export class UsageLimitsComponent implements OnInit, OnChanges {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();

  // Form properties
  maximumDiscountAmount: number | null = null;
  quantityLimit: number | null = null;
  usageType: 'single' | 'multiple' = 'single';
  multiUseLimit: number | null = null;

  // Validation properties
  errors: {
    maximumDiscountAmount?: string;
    quantityLimit?: string;
    usageType?: string;
    multiUseLimit?: string;
  } = {};

  isFormValid = true;

  ngOnInit(): void {
    this.syncFromCampaignData();
  }

  ngOnChanges(): void {
    this.syncFromCampaignData();
  }

  private syncFromCampaignData(): void {
    if (this.campaignData) {
      this.maximumDiscountAmount = this.campaignData.max_discount ?? null;
      this.quantityLimit = this.campaignData.quantity_limit ?? null;
      this.usageType = this.campaignData.usage_type || 'single';
      this.multiUseLimit = this.campaignData.multi_use_limit ?? null;
    }
  }

  onUsageLimitsToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onUsageLimitsDone(): void {
    if (this.validateForm()) {
      this.done.emit();
    }
  }

  // Increment/decrement methods
  incrementDiscountAmount(): void {
    const newValue = (this.maximumDiscountAmount || 0) + 1;
    // Enforce 9-digit maximum
    if (newValue <= 999999999) {
      this.maximumDiscountAmount = newValue;
      this.onFormChange();
    }
  }

  decrementDiscountAmount(): void {
    if (this.maximumDiscountAmount && this.maximumDiscountAmount > 0) {
      this.maximumDiscountAmount -= 1;
      this.onFormChange();
    }
  }

  isDiscountDecrementDisabled(): boolean {
    return !this.maximumDiscountAmount || this.maximumDiscountAmount <= 0;
  }

  incrementQuantityLimit(): void {
    const newValue = (this.quantityLimit || 0) + 1;
    // Enforce 9-digit maximum
    if (newValue <= 999999999) {
      this.quantityLimit = newValue;
      this.onFormChange();
    }
  }

  decrementQuantityLimit(): void {
    if (this.quantityLimit && this.quantityLimit > 0) {
      this.quantityLimit -= 1;
      this.onFormChange();
    }
  }

  isQuantityDecrementDisabled(): boolean {
    return !this.quantityLimit || this.quantityLimit <= 0;
  }

  incrementMultiUseLimit(): void {
    this.multiUseLimit = (this.multiUseLimit || 0) + 1;
    this.onFormChange();
  }

  decrementMultiUseLimit(): void {
    if (this.multiUseLimit && this.multiUseLimit > 0) {
      this.multiUseLimit -= 1;
      this.onFormChange();
    }
  }

  isMultiUseDecrementDisabled(): boolean {
    return !this.multiUseLimit || this.multiUseLimit <= 0;
  }

  selectUsageType(type: 'single' | 'multiple'): void {
    this.usageType = type;
    this.onFormChange();
  }

  preventMinusKey(event: KeyboardEvent): void {
    // Prevent minus key, hyphen, and minus on numpad
    if (
      event.key === '-' ||
      event.key === 'Minus' ||
      event.code === 'Minus' ||
      event.keyCode === 189 ||
      event.keyCode === 109
    ) {
      event.preventDefault();
    }
  }

  /**
   * Prevents negative numeric input for specific fields and enforces additional digit limits where applicable.
   * @example
   * reventNegativeValue('maximumDiscountAmount', event)
   * undefined
   * @param {{string}} {{field}} - The name of the field being validated ('maximumDiscountAmount', 'quantityLimit', or 'multiUseLimit').
   * @param {{Event}} {{event}} - The input event from an HTML input element.
   * @returns {{void}} No return value; the function updates the input value and component state as needed.
   **/
  preventNegativeValue(field: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const { value } = input;

    // If value is empty, allow it
    if (value === '') {
      return;
    }

    const numValue = parseFloat(value);

    // If the value is negative, reset it
    if (!isNaN(numValue) && numValue < 0) {
      input.value = '';
      if (field === 'maximumDiscountAmount') {
        this.maximumDiscountAmount = null;
      } else if (field === 'quantityLimit') {
        this.quantityLimit = null;
      } else if (field === 'multiUseLimit') {
        this.multiUseLimit = null;
      }
      this.onFormChange();
    }

    // For maximumDiscountAmount and quantityLimit, also enforce 9-digit limit
    if (field === 'maximumDiscountAmount' || field === 'quantityLimit') {
      this.enforceMaxDigits(field, event);
    }
  }

  /**
   * Enforces a maximum of 9 digits on a numeric input (excluding decimal point), truncates excess digits, updates the corresponding component property, and triggers a form change.
   * @example
   * nforceMaxDigits('maximumDiscountAmount', event)
   * undefined
   * @param {{string}} {{field}} - The name of the field to update ('maximumDiscountAmount' or 'quantityLimit').
   * @param {{Event}} {{event}} - The input DOM event containing the value to validate and possibly truncate.
   * @returns {{void}} Does not return a value.
   **/
  enforceMaxDigits(field: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const { value } = input;

    // If value is empty, allow it
    if (value === '') {
      return;
    }

    // Remove any non-digit characters (except decimal point for numbers)
    const numericValue = value.replace(/[^\d.]/g, '');

    // Check if the number has more than 9 digits (excluding decimal point)
    const digitsOnly = numericValue.replace('.', '');
    if (digitsOnly.length > 9) {
      // Truncate to 9 digits
      const truncated = digitsOnly.substring(0, 9);
      const numValue = parseFloat(truncated);

      if (field === 'maximumDiscountAmount') {
        this.maximumDiscountAmount = isNaN(numValue) ? null : numValue;
        input.value = isNaN(numValue) ? '' : numValue.toString();
      } else if (field === 'quantityLimit') {
        this.quantityLimit = isNaN(numValue) ? null : numValue;
        input.value = isNaN(numValue) ? '' : numValue.toString();
      }

      this.onFormChange();
    }
  }

  /**
   * Handle form changes: clear validation errors, normalize limit fields, and emit an updated usage-limits payload.
   * @example
   * nFormChange()
   * // Emits: { max_discount: 5000 | null, quantity_limit: 100 | null, usage_type: 'single' | 'multiple', multi_use_limit: 10 | null }
   * @returns {void} Emits a sanitized usage-limits object via this.dataChange event; does not return a value.
   */
  onFormChange(): void {
    // Clear errors when user starts typing
    this.clearErrors();

    // Ensure values are not negative and within limits
    if (this.maximumDiscountAmount !== null && this.maximumDiscountAmount < 0) {
      this.maximumDiscountAmount = null;
    }
    // Enforce 9-digit maximum
    if (this.maximumDiscountAmount !== null && this.maximumDiscountAmount > 999999999) {
      this.maximumDiscountAmount = 999999999;
    }
    if (this.quantityLimit !== null && this.quantityLimit < 0) {
      this.quantityLimit = null;
    }
    // Enforce 9-digit maximum for quantityLimit
    if (this.quantityLimit !== null && this.quantityLimit > 999999999) {
      this.quantityLimit = 999999999;
    }
    if (this.multiUseLimit !== null && this.multiUseLimit < 0) {
      this.multiUseLimit = null;
    }

    const data = {
      max_discount: this.maximumDiscountAmount,
      quantity_limit: this.quantityLimit,
      usage_type: this.usageType,
      multi_use_limit: this.usageType === 'multiple' ? this.multiUseLimit : null,
    };
    this.dataChange.emit(data);
  }

  // Validation methods
  /**
   * Validate the usage limits form: checks numeric limits (maximumDiscountAmount, quantityLimit), ensures usageType is provided, and enforces multi-use limits when usageType is 'multiple'.
   * @example
   * alidateForm()
   * true
   * @returns {boolean} Returns true when the form is valid; otherwise false.
   **/
  validateForm(): boolean {
    this.clearErrors();
    let isValid = true;

    // Validate maximum discount amount (9-digit limit)
    if (this.maximumDiscountAmount !== null && this.maximumDiscountAmount > 999999999) {
      this.errors.maximumDiscountAmount = 'Maximum discount amount cannot exceed 999,999,999';
      isValid = false;
    }

    // Validate quantity limit (9-digit limit)
    if (this.quantityLimit !== null && this.quantityLimit > 999999999) {
      this.errors.quantityLimit = 'Quantity limit cannot exceed 999,999,999';
      isValid = false;
    }

    // Validate usage type
    if (!this.usageType) {
      this.errors.usageType = 'Usage type is required';
      isValid = false;
    }

    if (
      this.usageType === 'multiple' &&
      this.multiUseLimit !== null &&
      this.multiUseLimit !== undefined &&
      this.multiUseLimit < 1
    ) {
      this.errors.multiUseLimit = 'Multi-use limit must be at least 1';
      isValid = false;
    }

    this.isFormValid = isValid;
    return isValid;
  }

  clearErrors(): void {
    this.errors = {};
    this.isFormValid = true;
  }

  // Method to check if a specific field has an error
  hasError(field: string): boolean {
    return Boolean(this.errors[field as keyof typeof this.errors]);
  }

  // Method to get error message for a specific field
  getError(field: string): string {
    return this.errors[field as keyof typeof this.errors] || '';
  }
}
